import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  useFieldArray,
  useFormState,
  useWatch,
  type Control,
  type UseFormSetValue,
} from 'react-hook-form';

import Button from '@/components/Button';
import AddressFields from '@/components/forms/AddressFields';
import { IconPlus } from '@/components/icon/tabler/IconPlus';
import { IconTrash } from '@/components/icon/tabler/IconTrash';
import OfferFormField from '@/domain/offers/components/OfferFormField';
import { type OfferFormValues } from '@/domain/offers/types';
import { OFFER_LIMITS, emptyOfferLocation, needsOfferLocation } from '@/domain/offers/utils';
import { useTranslate } from '@/state/i18n';
import { radiusTokens } from '@/theme/themePrimitives';

interface Props {
  control: Control<OfferFormValues>;
  setValue: UseFormSetValue<OfferFormValues>;
}

/** "Onde vale": optional places (the event address plus a name); required by "Como chegar". */
const OfferLocationsFields = ({ control, setValue }: Props) => {
  const t = useTranslate('domain.offers.components.OfferLocationsFields');
  const { fields, append, remove } = useFieldArray({ control, name: 'locations' });
  const actions = useWatch({ control, name: 'actions' }) ?? [];
  const { errors } = useFormState({ control, name: 'locations' });
  const listError = errors.locations?.root?.message ?? errors.locations?.message;

  return (
    <Stack gap={2.5}>
      {fields.length ? (
        fields.map((field, index) => (
          <Box key={field.id} sx={sx.card}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
              <Typography variant="subtitle2" fontWeight={700}>
                {t('position', { n: index + 1 })}
              </Typography>
              <Tooltip title={t('remove')}>
                <IconButton size="small" aria-label={t('remove')} onClick={() => remove(index)}>
                  <IconTrash fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <OfferFormField
              control={control}
              name={`locations.${index}.label`}
              label={t('label')}
              optionalLabel={t('optional')}
              placeholder={t('labelPlaceholder')}
              maxChars={OFFER_LIMITS.locationLabelMax}
            />
            <AddressFields control={control} setValue={setValue} name={`locations.${index}`} />
          </Box>
        ))
      ) : (
        <Typography variant="body2" color="text.secondary">
          {needsOfferLocation(actions) ? t('emptyRequired') : t('empty')}
        </Typography>
      )}

      {listError ? <FormHelperText error>{listError}</FormHelperText> : null}

      <Box>
        <Button
          variant="outlined"
          startIcon={<IconPlus />}
          disabled={fields.length >= OFFER_LIMITS.locationsMax}
          onClick={() => append(emptyOfferLocation())}
        >
          {fields.length ? t('addAnother') : t('add')}
        </Button>
      </Box>
    </Stack>
  );
};

export default OfferLocationsFields;

const sx = {
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2.5,
    p: 2,
    border: 1,
    borderColor: 'divider',
    borderRadius: radiusTokens.xl,
  },
};
